/**
 * Electrical + behavioral models for every Zapboard part.
 * Reference values: docs/02-prd.md §6. Tuned for a 3 V (2×AA) board.
 */
import type { PartProps } from '../core/types';
import type { PartDef, StepCtx } from './contract';

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));
/** Exponential approach with time constant `tau` (seconds). */
const approach = (cur: number, target: number, dt: number, tau: number) =>
  cur + (target - cur) * (1 - Math.exp(-dt / tau));

export const WIRE_OHMS = 0.01;
const twoPins = (len: number) => [0, len - 1] as const;

// ───────────────────────────── Battery ─────────────────────────────
export const BATTERY = {
  volts: 3.0,
  internalOhms: 0.5,
  shortAmps: 1.5,
  heatUpSeconds: 1.2,
  coolDownSeconds: 3,
};

export interface BatteryState {
  heat: number;
  shorted: boolean;
  tripped: boolean;
  hotAnnounced: boolean;
}

export const battery: PartDef<BatteryState> = {
  type: 'battery',
  name: 'Battery Pack',
  category: 'power',
  length: () => 4,
  pins: () => twoPins(4),
  defaultProps: () => ({}),
  initState: () => ({ heat: 0, shorted: false, tripped: false, hotAnnounced: false }),
  // Pin 0 is "+", pin 1 is "−".
  stamp(ctx, _p, s) {
    if (s.tripped) return; // safety cut-out: the pack rests until it cools down
    const pos = ctx.pin(0);
    const neg = ctx.pin(1);
    ctx.source(pos, neg, BATTERY.volts, BATTERY.internalOhms);
    // Through-current pin0→pin1 is minus the current pushed out of "+".
    ctx.measure((v) => -(BATTERY.volts - (v(pos) - v(neg))) / BATTERY.internalOhms);
  },
  step(c) {
    const { state: s } = c;
    const out = -c.current;
    const shorted = !s.tripped && out > BATTERY.shortAmps;
    if (shorted && !s.shorted) c.emit('short', { amps: round2(out) });
    s.shorted = shorted;
    s.heat = clamp(s.heat + (shorted ? c.dt / BATTERY.heatUpSeconds : -c.dt / BATTERY.coolDownSeconds), 0, 1);
    if (s.heat > 0.45 && !s.hotAnnounced) {
      s.hotAnnounced = true;
      c.emit('hot');
    }
    if (s.heat >= 1 && !s.tripped) {
      s.tripped = true;
      s.shorted = false;
      c.emit('tripped');
    }
    if (s.heat === 0 && (s.tripped || s.hotAnnounced)) {
      s.tripped = false;
      s.hotAnnounced = false;
      c.emit('cool');
    }
  },
  facts: {
    what: 'The battery is the pusher! It pushes electricity around the loop and back home again.',
    home: 'Batteries live in TV remotes, toys, flashlights, and clocks.',
    real: 'This pack holds two AA batteries. Each one gives 1.5 volts, so together they make 3 volts.',
  },
};

// ───────────────────────────── Snap wire ─────────────────────────────
/** Straight snap-wires stretch to any length between these. */
export const WIRE_MIN = 2;
export const WIRE_MAX = 14;
const wireLen = (p: PartProps) => clamp(Math.round(Number(p.length ?? 2)), WIRE_MIN, WIRE_MAX);

const wireStamp: PartDef['stamp'] = (ctx) => {
  const a = ctx.pin(0);
  const b = ctx.pin(1);
  ctx.resistor(a, b, WIRE_OHMS);
  ctx.measure((v) => (v(a) - v(b)) / WIRE_OHMS);
};

const wireFacts = {
  what: 'Wires are roads for electricity. They connect parts so electricity can travel between them.',
  home: 'There are wires hiding inside walls, chargers, and every gadget you own.',
  real: 'Real wires are made of copper metal inside, with a plastic coat outside to keep fingers safe.',
};

export const wire: PartDef = {
  type: 'wire',
  name: 'Snap Wire',
  category: 'wire',
  length: wireLen,
  pins: (p) => twoPins(wireLen(p)),
  defaultProps: () => ({ length: 2, color: 'blue' }),
  initState: () => ({}),
  stamp: wireStamp,
  facts: wireFacts,
};

/** A bendy cable between any two studs. It flies over everything in between. */
export const jumper: PartDef = {
  type: 'jumper',
  name: 'Jumper Cable',
  category: 'wire',
  length: () => 2,
  pins: () => [0, 1],
  shape: (p) => [
    { dx: 0, dy: 0, pin: 0 },
    { dx: Math.round(Number(p.dx ?? 1)), dy: Math.round(Number(p.dy ?? 1)), pin: 1 },
  ],
  defaultProps: () => ({ dx: 2, dy: 1, color: 'orange' }),
  initState: () => ({}),
  stamp: wireStamp,
  facts: {
    what: 'A jumper cable is a bendy wire. It can jump over other parts to connect two snaps anywhere!',
    home: 'Phone chargers and headphone cables are bendy wires, just like this one.',
    real: 'Bendy cables have lots of very thin copper threads inside, so they can bend without breaking.',
  },
};

// ───────────────────────────── Light bulb ─────────────────────────────
export const LAMP = { ohms: 10, ratedWatts: 0.8, warmTau: 0.08, coolTau: 0.14 };

export interface GlowState {
  brightness: number;
  on: boolean;
}

const glowEvents = (c: StepCtx<GlowState>) => {
  const s = c.state;
  if (!s.on && s.brightness > 0.25) {
    s.on = true;
    c.emit('lit');
  } else if (s.on && s.brightness < 0.12) {
    s.on = false;
    c.emit('dark');
  }
};

export const lamp: PartDef<GlowState> = {
  type: 'lamp',
  name: 'Light Bulb',
  category: 'light',
  length: () => 3,
  pins: () => twoPins(3),
  defaultProps: () => ({}),
  initState: () => ({ brightness: 0, on: false }),
  stamp(ctx) {
    const a = ctx.pin(0);
    const b = ctx.pin(1);
    ctx.resistor(a, b, LAMP.ohms);
    ctx.measure((v) => (v(a) - v(b)) / LAMP.ohms);
  },
  step(c) {
    const power = c.current * c.current * LAMP.ohms;
    const target = clamp(power / LAMP.ratedWatts, 0, 1.25);
    const tau = target > c.state.brightness ? LAMP.warmTau : LAMP.coolTau;
    c.state.brightness = approach(c.state.brightness, target, c.dt, tau);
    glowEvents(c);
  },
  facts: {
    what: 'When electricity squeezes through the tiny wire inside the bulb, it gets so hot that it glows!',
    home: 'Old lamps, flashlights, and fairy lights use bulbs like this.',
    real: 'Bulbs get warm because they turn a lot of electricity into heat. LEDs make the same light with much less.',
  },
};

// ───────────────────────────── LED ─────────────────────────────
export const LED_COLORS = {
  red: { vf: 1.8, hex: '#ff3b3b' },
  yellow: { vf: 2.0, hex: '#ffd23b' },
  green: { vf: 2.1, hex: '#34e07a' },
  blue: { vf: 2.6, hex: '#3b8bff' },
} as const;
export type LedColor = keyof typeof LED_COLORS;
export const LED = { seriesOhms: 100, refAmps: 0.015, overAmps: 0.03, nvt: 0.0517 };

export interface LedState extends GlowState {
  reversed: boolean;
  overdriven: boolean;
}

/** Saturation current so the LED drops `vf` at the reference current. */
const ledIs = (vf: number) => LED.refAmps / Math.exp(vf / LED.nvt);

export const led: PartDef<LedState> = {
  type: 'led',
  name: 'LED',
  category: 'light',
  length: () => 3,
  pins: () => twoPins(3),
  defaultProps: () => ({ color: 'red' }),
  initState: () => ({ brightness: 0, on: false, reversed: false, overdriven: false }),
  // Pin 0 is the "+" side (anode). A built-in resistor keeps it safe on 3 V, like real kit LEDs.
  stamp(ctx, part) {
    const a = ctx.pin(0);
    const b = ctx.pin(1);
    const m = ctx.internal();
    const color = LED_COLORS[(part.props.color as LedColor) ?? 'red'] ?? LED_COLORS.red;
    ctx.resistor(a, m, LED.seriesOhms);
    ctx.diode(m, b, ledIs(color.vf), LED.nvt);
    ctx.measure((v) => (v(a) - v(m)) / LED.seriesOhms);
  },
  step(c) {
    const s = c.state;
    const target = clamp(c.current / LED.refAmps, 0, 1.3);
    s.brightness = approach(s.brightness, target, c.dt, 0.012);
    const reversed = c.voltage < -1.2;
    if (reversed && !s.reversed) c.emit('reversed');
    s.reversed = reversed;
    const over = c.current > LED.overAmps;
    if (over && !s.overdriven) c.emit('overdrive');
    s.overdriven = over;
    glowEvents(c);
  },
  facts: {
    what: 'An LED is a light that only works one way, like a one-way street. Flip it and it goes dark!',
    home: 'LEDs are in TV lights, phone screens, traffic lights, and Christmas lights.',
    real: 'LED stands for Light Emitting Diode. It uses about five times less electricity than an old bulb.',
  },
};

// ───────────────────────────── Motor + fan ─────────────────────────────
export const MOTOR = {
  ohms: 5,
  k: 0.05, // V per rad/s, and N·m per A
  inertia: 1.6e-4,
  dragWithFan: 9e-5,
  dragFree: 2e-5,
  friction: 6e-4, // N·m of Coulomb friction
  /** rad/s: spin faster than this, cut the power, and the fan flies. */
  launchOmega: 30,
  fanAwaySeconds: 2.8,
  spinOmega: 4,
};

export interface MotorState {
  omega: number;
  fanAttached: boolean;
  /** Seconds since the fan launched (while it's flying). */
  fanAway: number;
  /** Speed at launch, so the flight can be animated. */
  launchOmega: number;
  spinning: boolean;
  lastCurrent: number;
  launches: number;
}

export const motor: PartDef<MotorState> = {
  type: 'motor',
  name: 'Motor & Fan',
  category: 'motion',
  length: () => 3,
  pins: () => twoPins(3),
  defaultProps: () => ({}),
  initState: () => ({
    omega: 0,
    fanAttached: true,
    fanAway: 0,
    launchOmega: 0,
    spinning: false,
    lastCurrent: 0,
    launches: 0,
  }),
  stamp(ctx, _p, s) {
    const a = ctx.pin(0);
    const b = ctx.pin(1);
    // Spinning motors push back (back-EMF): a source opposing the supply.
    const emf = MOTOR.k * s.omega;
    ctx.source(a, b, emf, MOTOR.ohms);
    ctx.measure((v) => (v(a) - v(b) - emf) / MOTOR.ohms);
  },
  step(c) {
    const s = c.state;
    const drag = s.fanAttached ? MOTOR.dragWithFan : MOTOR.dragFree;
    const torque = MOTOR.k * c.current;
    // Sub-step the mechanics for stability. Coulomb friction lets a coasting fan actually stop.
    const n = 4;
    const h = c.dt / n;
    for (let k = 0; k < n; k++) {
      const accel = (torque - drag * s.omega) / MOTOR.inertia;
      const fric = (MOTOR.friction / MOTOR.inertia) * h;
      let next = s.omega + accel * h;
      if (Math.abs(next) <= fric && Math.abs(torque) < MOTOR.friction) next = 0;
      else next -= Math.sign(next) * fric;
      s.omega = next;
    }

    const spinning = Math.abs(s.omega) > MOTOR.spinOmega;
    if (spinning && !s.spinning) c.emit('spin', { dir: Math.sign(s.omega) });
    if (!spinning && s.spinning) c.emit('stop');
    s.spinning = spinning;

    // Power cut while spinning fast: the fan keeps going and lifts off (if it spins the lifting way).
    const powerCut = Math.abs(s.lastCurrent) > 0.02 && Math.abs(c.current) < 0.004;
    if (powerCut && s.fanAttached) {
      if (s.omega > MOTOR.launchOmega) {
        s.fanAttached = false;
        s.fanAway = 0;
        s.launchOmega = s.omega;
        s.launches++;
        c.emit('launch', { omega: round2(s.omega) });
      } else if (s.omega < -MOTOR.launchOmega) {
        c.emit('wrongway');
      }
    }
    if (!s.fanAttached) {
      s.fanAway += c.dt;
      if (s.fanAway >= MOTOR.fanAwaySeconds) {
        s.fanAttached = true;
        s.fanAway = 0;
        c.emit('landed');
      }
    }
    s.lastCurrent = c.current;
  },
  facts: {
    what: 'A motor turns electricity into spinning! Magnets inside push and pull to make it turn.',
    home: 'Motors spin fans, washing machines, electric toothbrushes, and toy cars.',
    real: 'Flip the battery around and a motor spins the other way. Electric cars use giant motors!',
  },
};

// ───────────────────────────── Switches ─────────────────────────────
const switchStamp: PartDef['stamp'] = (ctx) => {
  // Closed = a short piece of wire. Open = nothing at all.
  const a = ctx.pin(0);
  const b = ctx.pin(1);
  ctx.resistor(a, b, WIRE_OHMS);
  ctx.measure((v) => (v(a) - v(b)) / WIRE_OHMS);
};

export const slideSwitch: PartDef = {
  type: 'switch',
  name: 'Switch',
  category: 'control',
  length: () => 3,
  pins: () => twoPins(3),
  defaultProps: () => ({ on: false }),
  initState: () => ({}),
  interaction: 'toggle',
  stamp(ctx, part, state, input) {
    if (part.props.on) switchStamp(ctx, part, state, input);
  },
  facts: {
    what: 'A switch opens and closes a gate in the loop. Closed gate: electricity flows. Open gate: it stops.',
    home: 'Light switches on your wall work exactly like this!',
    real: 'Inside a switch, two pieces of metal touch to let electricity through, and move apart to stop it.',
  },
};

export const button: PartDef = {
  type: 'button',
  name: 'Press Button',
  category: 'control',
  length: () => 3,
  pins: () => twoPins(3),
  defaultProps: () => ({}),
  initState: () => ({}),
  interaction: 'hold',
  stamp(ctx, part, state, input) {
    if (input.pressed) switchStamp(ctx, part, state, input);
  },
  facts: {
    what: 'A press button only lets electricity through while you hold it down.',
    home: 'Doorbells, keyboards, and game controllers are full of press buttons.',
    real: 'A spring pushes the button back up, so the gate opens again when you let go.',
  },
};

// ───────────────────────────── Buzzer ─────────────────────────────
export const BUZZER = { ohms: 60, onAmps: 0.005, fullAmps: 0.04 };

export interface BuzzerState {
  level: number;
  sounding: boolean;
}

export const buzzer: PartDef<BuzzerState> = {
  type: 'buzzer',
  name: 'Buzzer',
  category: 'sound',
  length: () => 3,
  pins: () => twoPins(3),
  defaultProps: () => ({}),
  initState: () => ({ level: 0, sounding: false }),
  stamp(ctx) {
    const a = ctx.pin(0);
    const b = ctx.pin(1);
    ctx.resistor(a, b, BUZZER.ohms);
    ctx.measure((v) => (v(a) - v(b)) / BUZZER.ohms);
  },
  step(c) {
    const s = c.state;
    const amps = Math.abs(c.current);
    s.level = amps > BUZZER.onAmps ? clamp(amps / BUZZER.fullAmps, 0.2, 1) : 0;
    const sounding = s.level > 0;
    if (sounding && !s.sounding) c.emit('buzz');
    if (!sounding && s.sounding) c.emit('quiet');
    s.sounding = sounding;
  },
  facts: {
    what: 'A buzzer wiggles a thin metal plate super fast, and the wiggles make a sound!',
    home: 'Microwaves, alarm clocks, and smoke alarms beep with buzzers.',
    real: 'The plate wiggles thousands of times every second. That is how fast sound waves are!',
  },
};

// ───────────────────────────── Resistor ─────────────────────────────
export const resistor: PartDef = {
  type: 'resistor',
  name: 'Resistor',
  category: 'control',
  length: () => 3,
  pins: () => twoPins(3),
  defaultProps: () => ({ ohms: 100 }),
  initState: () => ({}),
  stamp(ctx, part) {
    const a = ctx.pin(0);
    const b = ctx.pin(1);
    const r = Math.max(1, Number(part.props.ohms ?? 100));
    ctx.resistor(a, b, r);
    ctx.measure((v) => (v(a) - v(b)) / r);
  },
  facts: {
    what: 'A resistor is a speed bump for electricity. It slows the flow down so other parts stay safe.',
    home: 'Every gadget, from phones to TVs, has tiny resistors inside.',
    real: 'The colored stripes on a real resistor are a secret code that tells how big the speed bump is.',
  },
};

function round2(x: number) {
  return Math.round(x * 100) / 100;
}
